import { extractPlaceholders, MERCHANT_RISK_CONFIG_FIELDS } from "@unbox/api";
import type { RiskModelType } from "@unbox/db";
import { parseKeywordList, serializeKeywordList } from "@/utils/keyword-lists";

export { parseKeywordList, serializeKeywordList };

// The model type's keyword (stringArray) config fields — the only detection
// config a merchant may edit directly (e.g. banned_keywords, expected_strings).
export const keywordFieldsFor = (modelType: RiskModelType): string[] =>
  MERCHANT_RISK_CONFIG_FIELDS[modelType].filter((f) => f.type === "stringArray").map((f) => f.key);

const SYSTEM_RESOLVED_PLACEHOLDERS = new Set(["brand_name"]);

// Placeholders whose values are owned by another screen (Merchant Details) rather
// than edited inline in the safety modal. They still render here, but read-only,
// pointing the user to where they're managed.
export const MERCHANT_MANAGED_PLACEHOLDERS = new Set(["own_brands"]);

// Display order for keyword/placeholder lists in the config UI (matches Figma).
// Keys not listed keep their original relative order, placed after the listed ones.
const KEYWORD_FIELD_ORDER = ["own_brands", "licensed_collabs", "competitors_to_flag"];

export const orderKeywordKeys = (keys: string[]): string[] => {
  const rank = (key: string) => {
    const i = KEYWORD_FIELD_ORDER.indexOf(key);
    return i === -1 ? KEYWORD_FIELD_ORDER.length : i;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b));
};

// Second sentence appended to the read-only `own_brands` label, phrased per risk
// (matches Figma). The third-party-logo copy is provisional pending product sign-off.
export const OWN_BRANDS_LABEL_SUFFIX: Record<string, string> = {
  competitor_sentiment: "These won't be flagged as competitor disparagement.",
  third_party_logo_present: "These won't be flagged as third-party logos.",
};

// Domain-knowledge keys referenced as `{placeholder}` tokens in the model type's
// string config fields (read from the effective config).
export const placeholderKeysFor = (modelType: RiskModelType, config: Record<string, unknown>): string[] => {
  const keys = new Set<string>();
  for (const field of MERCHANT_RISK_CONFIG_FIELDS[modelType]) {
    if (field.type !== "string") continue;
    const value = config[field.key];
    if (typeof value === "string") for (const k of extractPlaceholders(value)) keys.add(k);
  }
  return [...keys].filter((k) => !SYSTEM_RESOLVED_PLACEHOLDERS.has(k));
};

// Readable labels for the editable keyword config fields.
export const KEYWORD_FIELD_LABELS: Record<string, string> = {
  banned_keywords: "Banned Keywords to flag",
  expected_strings: "Required hashtags or captions",
  approved_claims: "Approved claims",
  competitors_to_flag: "Specific competitors you would like to be flagged",
  licensed_collabs: "Brands you're collaborating with that should not be flagged",
  own_brands: "Brands you own or are associated with",
  normal_portion_definition:
    "Describe a standard portion size (e.g 'no more than three packets'). Any consumption in excess of this description will be flagged",
};

export const humanizeKey = (key: string): string => key.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// One editable/displayable keyword list — either a model type keyword field
// (`source: "config"`) or a `{placeholder}` backed by domain knowledge
// (`source: "domainKnowledge"`). `config` is the effective merchant config.
export type KeywordGroup = {
  key: string;
  label: string;
  values: string[];
  source: "config" | "domainKnowledge";
};

export const buildKeywordGroups = (
  modelType: RiskModelType,
  config: Record<string, unknown>,
  domainKnowledge: Record<string, string>,
): KeywordGroup[] => {
  const groups: KeywordGroup[] = [];
  for (const field of keywordFieldsFor(modelType)) {
    const value = config[field];
    groups.push({
      key: field,
      label: KEYWORD_FIELD_LABELS[field] ?? humanizeKey(field),
      values: Array.isArray(value) ? (value as string[]) : [],
      source: "config",
    });
  }
  for (const key of placeholderKeysFor(modelType, config)) {
    groups.push({
      key,
      label: humanizeKey(key),
      values: parseKeywordList(domainKnowledge[key]),
      source: "domainKnowledge",
    });
  }
  return groups;
};
